import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { zodResolver } from '@hookform/resolvers/zod';
import { RootState } from 'store';

import { Close } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Autocomplete, Box, Drawer, Modal, TextField, Typography } from '@mui/material';
import FormHelperText from '@mui/material/FormHelperText';

import CustomButton from 'components/Button';
import { Header } from 'components/GlobalStyles/styles';
import LabelledTextField from 'components/LabelledTextField';
import { MessageType } from 'components/SnackBar';
import {
  useAddProjectClientMutation,
  useDeleteProjectClientContactMutation,
  useGetProjectClientQuery,
  useUpdateClientContactByIdMutation,
} from 'services/busmanApi';
import { ClientProjectRequest } from 'types/ProjectTypes';
import { AddProjectClientSchema } from 'utils/schema';

import { ColContainer, FormSection, RowContainer } from '../styles';
interface ProjectClientProps {
  open: boolean;
  onClose: () => void;
  isEditing?: boolean;
  initialValues: ClientProjectRequest | null;
  setSnackBarMessage: (message: MessageType) => void;
}

const ClientContactModal: React.FC<ProjectClientProps> = ({
  initialValues,
  open,
  onClose,
  isEditing,
  setSnackBarMessage,
}) => {
  const { clients } = useSelector((state: RootState) => state.worklogs);
  const [deleteClientContact, setDeleteClientContact] = React.useState<boolean>(false);
  const [addNewClient] = useAddProjectClientMutation();
  const [deleteClient] = useDeleteProjectClientContactMutation();
  const [updateClient] = useUpdateClientContactByIdMutation();
  const { refetch } = useGetProjectClientQuery({});

  const defaultValues: ClientProjectRequest = {
    client: '',
    email: '',
    phone: '',
    position: '',
    full_name: '',
  };

  const {
    handleSubmit,
    control,
    formState: { isDirty, errors },
    setValue,
  } = useForm({
    defaultValues: initialValues ?? defaultValues,
    resolver: zodResolver(AddProjectClientSchema),
  });

  const primaryContactErrors = errors.position || errors.full_name || errors.email || errors.phone;

  const onSubmit = async (data: ClientProjectRequest) => {
    const action =
      deleteClientContact && isEditing
        ? { request: () => deleteClient(data.id), label: 'deleting' as const, success: 'deleted' as const }
        : isEditing && initialValues
        ? { request: () => updateClient(data), label: 'updating' as const, success: 'updated' as const }
        : { request: () => addNewClient(data), label: 'adding new' as const, success: 'added' as const };

    const response = await action.request();
    if (response.error) {
      setSnackBarMessage({
        type: 'error',
        msg: `Error ${action.label} project contact`,
      });
    } else {
      refetch();
      setSnackBarMessage({
        type: 'success',
        msg: `Project Contact ${action.success} successfully.`,
      });
    }
    onClose();
  };
  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <Box
          role="presentation"
          sx={{ borderRadius: 2, width: { xs: '100vw', sm: 480 }, maxWidth: '100vw', p: { xs: 2, sm: 3 } }}>
          <Header>
            <Typography variant="h4">{isEditing ? 'Edit Project Contact' : 'New Project Contact'}</Typography>
            <Box onClick={onClose} sx={{ cursor: 'pointer' }}>
              <Close sx={{ color: 'text.disabled', fontSize: '18px' }} />
            </Box>
          </Header>

          <FormSection>
            <RowContainer>
              <Controller
                name="client"
                control={control}
                render={({ field, fieldState: { error } }) => {
                  const clientsList = clients || [];
                  return (
                    <ColContainer>
                      <Typography variant="body2">CLIENT</Typography>
                      <Autocomplete
                        size="small"
                        options={clientsList}
                        value={clientsList.find((client) => client.client_id === field.value) || null}
                        getOptionLabel={(option) => option.client_name || ''}
                        isOptionEqualToValue={(option, value) => option.client_id === value?.client_id}
                        onChange={(event, value) => {
                          field.onChange(value?.client_id || '');
                          setValue('client', value?.client_id || '');
                        }}
                        ListboxProps={{
                          style: {
                            fontSize: '12px',
                          },
                        }}
                        sx={{ width: '100%' }}
                        data-testid="client-select-option"
                        popupIcon={<ExpandMoreIcon fontSize="small" />}
                        renderInput={(params) => <TextField {...params} required error={!!error} />}
                      />
                      {error && <FormHelperText sx={{ color: 'red' }}>*{error.message || error.type}</FormHelperText>}
                    </ColContainer>
                  );
                }}
              />
            </RowContainer>

            {/* Primary Contact */}
            <Box sx={{ my: 2 }}>
              <Typography variant="h5" sx={{ color: primaryContactErrors && 'red' }}>
                Primary Contact
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 2 }}>
                <RowContainer>
                  <LabelledTextField name="position" label="POSITION OR ROLE" control={control} />
                  <LabelledTextField name="full_name" label="FULL NAME" control={control} />
                </RowContainer>
                <RowContainer>
                  <LabelledTextField name="email" label="EMAIL" control={control} />
                  <LabelledTextField name="phone" label="PHONE (OPTIONAL)" control={control} />
                </RowContainer>
              </Box>
            </Box>

            <RowContainer sx={{ justifyContent: `${isEditing ? 'space-between' : 'flex-end'}`, mt: 2 }}>
              {isEditing && (
                <CustomButton
                  text={'Delete'}
                  variant="contained_red"
                  width="medium"
                  testid={'delete-client-modal-btn'}
                  onClick={() => setDeleteClientContact(true)}
                />
              )}
              <CustomButton
                text={`${isEditing ? 'Update' : 'Add'}`}
                variant="contained"
                width="large"
                testid={'add-update-client-modal-btn'}
                onClick={handleSubmit(onSubmit)}
                disabled={isEditing && !isDirty}
              />
            </RowContainer>
          </FormSection>
        </Box>
      </Drawer>
      <Modal
        open={deleteClientContact}
        onClose={() => setDeleteClientContact(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <Box
          sx={{
            p: 2,
            top: '50%',
            left: '50%',
            width: 450,
            position: 'absolute',
            bgcolor: 'background.paper',
            transform: 'translate(-50%, -50%)',
            boxShadow:
              '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
          }}>
          <Header>
            <Typography variant="h4">Delete Project</Typography>
            <Box onClick={() => setDeleteClientContact(false)} sx={{ cursor: 'pointer' }}>
              <Close sx={{ color: 'text.disabled', fontSize: '18px' }} />
            </Box>
          </Header>
          <Typography variant="body1" sx={{ mt: 2 }}>
            Are you sure you want to delete contact details for {initialValues?.full_name} ?
          </Typography>
          <RowContainer sx={{ justifyContent: 'flex-end', mt: 2 }}>
            <CustomButton
              text={'Delete'}
              variant="contained_red"
              width="medium"
              testid={'delete-client-modal-btn'}
              onClick={handleSubmit(onSubmit)}
            />
          </RowContainer>
        </Box>
      </Modal>
    </>
  );
};

export default ClientContactModal;
